import React, { useContext } from 'react';
import moment from 'moment-timezone';
// weekday abbreviations for the summary lines — vite does not bundle moment locales implicitly
import 'moment/dist/locale/fi';
import 'moment/dist/locale/en-gb';
import { translateService } from '../../services/TranslateService';
import { DataContext } from '../../contexts/dataContext';
import { ActiveFilterIndicators } from './ActiveFilterIndicators/ActiveFilterIndicators';
import styles from './SearchSummaryBar.module.scss';

type SearchLocation = { formatted_address?: string; name?: string } | string | null;

declare type SearchSummaryBarProps = {
    location: SearchLocation;
    pickupDate?: moment.Moment | null;
    returnDate?: moment.Moment | null;
    onEdit: () => void;
    onMapToggle?: () => void; // opens the map on the list view, closes it on the map view
    showMapButton?: boolean;
    mapOpen?: boolean;       // bar is on the fullscreen map view — map button becomes "Sulje kartta"
    overlay?: boolean;       // absolute overlay positioning (used on the map view)
    setOpenFilters?: (open: boolean) => void;
    loading?: boolean;
};

const shortLocation = (location: SearchLocation): string => {
    if (!location) { return ''; }
    if (typeof location === 'string') { return location; }
    return location.formatted_address ?? location.name ?? '';
};

/**
 * Compact sticky bar shown on mobile instead of the full search form once results are
 * visible. Shows the current search (location + dates), always one tap away from
 * editing, and hosts the map toggle so no floating button needs to overlap the results.
 */
export const SearchSummaryBar = (props: SearchSummaryBarProps) => {
    const dataContext = useContext(DataContext);
    const momentLocale = dataContext.language === 'fi' ? 'fi' : 'en-gb';
    // labeled pickup/return lines with capitalized weekday, e.g. "Nouto: Ke 6.8. - 09:00"
    const dateLine = (labelKey: string, date: moment.Moment) => {
        const m = moment(date).locale(momentLocale);
        const weekday = m.format('dd');
        const weekdayCapitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
        return `${translateService.translate(labelKey)}: ${weekdayCapitalized} ${m.format('D.M.')} - ${m.format('HH:mm')}`;
    };

    // Measure any fixed/sticky header so the bar sits below it — covers standalone
    // (.loginHeader/.loginHeaderWP) and WP/Divi (#main-header, .et-l--header) headers.
    const [topOffset, setTopOffset] = React.useState(0);
    React.useEffect(() => {
        const selectors = [
            '#wpadminbar',      // WP admin bar (logged into WP dashboard)
            '#main-header',     // Divi 4 theme header
            '.et-l--header',    // Divi 5 theme header
            '.loginHeader',     // Standalone login header
            '.loginHeaderWP',   // WP login header variant
        ];
        let total = 0;
        for (const sel of selectors) {
            const el = document.querySelector(sel);
            if (el) {
                const pos = getComputedStyle(el).position;
                if (pos === 'fixed' || pos === 'sticky') {
                    total += Math.round(el.getBoundingClientRect().height);
                }
            }
        }
        setTopOffset(total);
    }, [props.overlay]);

    const editLabel = translateService.translate('vehicleSearch1.editSearch');
    const mapLabel = translateService.translate(props.mapOpen ? 'briefingPhase.closeMapButton' : 'vehicleSearch1.mapShort');
    const vehicleSelected = !!dataContext.vehicle;

    // Resolve display values with fallbacks from searchParameters for URL-param-initiated searches
    // where pickupDate/returnDate or location may not be set yet.
    const sp = dataContext.searchParameters;
    const displayLocation = sp?.userPlaceSearch
        || props.location
        || sp?.searchCity
        || sp?.location;
    const displayPickup = props.pickupDate || (sp?.startDate ? moment(sp.startDate) : null);
    const displayReturn = props.returnDate || (sp?.endDate ? moment(sp.endDate) : null);

    return (
        <div className={`${styles.summaryBar} ${props.overlay ? styles.overlayBar : ''}`}
             style={{ top: props.overlay ? topOffset : 0 }} role="region"
             aria-label={translateService.translate('vehicleSearch1.currentSearchSummary')}>
            <div className={styles.summaryRow}>
                <button type="button" className={styles.summaryContent}
                        onClick={vehicleSelected ? undefined : props.onEdit}
                        style={vehicleSelected ? { cursor: 'default' } : undefined}
                        aria-label={editLabel}>
                    <span className={styles.summaryLocation}>{shortLocation(displayLocation)}</span>
                    {displayPickup && displayReturn && <>
                        <span className={styles.summaryDates}>{dateLine('vehicleSearch1.pickup', displayPickup)}</span>
                        <span className={styles.summaryDates}>{dateLine('vehicleSearch1.dropoff', displayReturn)}</span>
                    </>}
                </button>
                {!vehicleSelected &&
                    <button type="button" className={`btn btn-primary btn-outline-primary ${styles.editButton}`}
                            onClick={props.onEdit} aria-label={editLabel} title={editLabel}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                        </svg>
                        <span className={styles.editLabel}>{translateService.translate('vehicleSearch1.editSearchLong')}</span>
                    </button>}
                {props.showMapButton &&
                    <button type="button" className={`btn btn-secondary ${styles.mapButton}`}
                            onClick={props.onMapToggle} aria-label={mapLabel} title={mapLabel}>
                        {props.mapOpen ?
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                                <path d="M18 6 6 18M6 6l12 12"/>
                            </svg>
                            :
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z"/><path d="M9 3v15M15 6v15"/>
                            </svg>}
                        <span className={styles.mapButtonLabel}>{mapLabel}</span>
                    </button>}
            </div>
            {!vehicleSelected && props.setOpenFilters &&
                <ActiveFilterIndicators setOpenFilters={props.setOpenFilters} compact loading={props.loading} />}
        </div>
    );
};

export default SearchSummaryBar;
